import { AnswerVisual, type TechnicalEmbed } from '@paladigm/answer-visuals';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FileCard } from '@shared/components/files';
import { InertImage } from '@shared/components/markdown/InertImage';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@shared/components/ui';
import { cn } from '@shared/components/ui/cn';

import { useVaultFileLink } from '../../hooks/use-vault-file-link';
import { fetchChartToken } from '../../service/orchestrator-client';
import type { ContentBlock, PendingResultFile } from '../../types';
import { useDocumentTheme, useVisualWidth } from '../../visuals';
import { Markdown } from '../Markdown';

import { ChartBlock } from './ChartBlock';
import {
  formatCell,
  formatMetricValue,
  linkExpiryMs,
  safeHref,
  safeString,
  signClass,
} from './format';

const RENDERABLE = new Set<ContentBlock['type']>([
  'text',
  'metric_card',
  'metric_group',
  'table',
  'chart',
  'image',
  'file',
  'document',
  'list',
  'code',
  'agent_card',
  'error_block',
  'visual',
]);

interface BlockListProps {
  blocks: ContentBlock[];
  order?: string[];
  pendingFiles?: PendingResultFile[];
  onTryAgain?: () => void;
}

/** Renders an ordered list of blocks (manifest order, with metric and file grouping). */
export function BlockList({ blocks, order, pendingFiles = [], onTryAgain }: BlockListProps) {
  const ordered = orderBlocks(blocks, order).filter((b) => RENDERABLE.has(b.type));
  if (!ordered.length && !pendingFiles.length) return null;

  // Group consecutive metric cards into one row and consecutive files into one stack.
  const groups: (ContentBlock | BlockGroup)[] = [];
  let lastFileStack: BlockGroup | undefined;
  for (const b of ordered) {
    const last = groups.at(-1);
    if (b.type !== 'metric_card' && b.type !== 'file') groups.push(b);
    else if (last && 'blocks' in last && last.type === b.type) last.blocks.push(b);
    else {
      const group: BlockGroup = { type: b.type, blocks: [b] };
      if (b.type === 'file') lastFileStack = group;
      groups.push(group);
    }
  }
  if (pendingFiles.length) {
    if (lastFileStack) lastFileStack.pending = pendingFiles;
    else groups.push({ type: 'file', blocks: [], pending: pendingFiles });
  }

  return (
    <div className="mt-2 flex flex-col gap-3">
      {groups.map((g, i) => {
        if (!('blocks' in g)) return <BlockRenderer key={g.id} block={g} />;
        const key = `${g.type}-${g.blocks[0]?.id ?? (g.pending ? 'pending' : String(i))}`;
        return g.type === 'metric_card' ? (
          <div key={key} className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {g.blocks.map((b) => (
              <MetricCard key={b.id} block={b} />
            ))}
          </div>
        ) : (
          <div key={key} className="flex flex-col gap-2">
            {g.blocks.map((b) => (
              <BlockRenderer key={b.id} block={b} />
            ))}
            {g.pending?.map((file) => {
              const failed = file.state === 'failed';
              return (
                <FileCard
                  key={`pending:${file.key}`}
                  state={failed ? 'result-failed' : 'preparing'}
                  name={file.name}
                  className="w-full"
                  onTryAgain={failed ? onTryAgain : undefined}
                />
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

interface BlockGroup {
  type: 'metric_card' | 'file';
  blocks: ContentBlock[];
  pending?: PendingResultFile[];
}

function orderBlocks(blocks: ContentBlock[], order?: string[]): ContentBlock[] {
  if (!order?.length) return blocks;
  const byId = new Map(blocks.map((b) => [b.id, b]));
  const ordered = order.map((id) => byId.get(id)).filter((b): b is ContentBlock => Boolean(b));
  for (const b of blocks) if (!order.includes(b.id)) ordered.push(b);
  return ordered;
}

export function BlockRenderer({ block }: { block: ContentBlock }) {
  switch (block.type) {
    case 'text':
    case 'document':
      return <DocumentOrText block={block} />;
    case 'metric_card':
      return <MetricCard block={block} />;
    case 'metric_group':
      return <MetricGroup block={block} />;
    case 'table':
      return <TableBlock block={block} />;
    case 'chart':
      return <ChartBlock block={block} />;
    case 'image':
      return <ImageBlock block={block} />;
    case 'file':
      return <FileBlock block={block} />;
    case 'list':
      return <ListBlock block={block} />;
    case 'code':
      return <CodeBlock block={block} />;
    case 'agent_card':
      return <AgentCard block={block} />;
    case 'error_block':
      return <ErrorBlock block={block} />;
    case 'visual':
      return <VisualBlock block={block} />;
    default:
      return null;
  }
}

// ── visual ───────────────────────────────────────────────────────────────────

/**
 * A `technical` visual's chart: trade-system's embed, framed from the origin its token
 * names. The token comes from the orchestrator's chart-token route through the `/ai-cio`
 * passthrough, under the CRM user's bearer; a refusal rejects, and the card shows its
 * readings. "Open in Trade System" sends a signed-out user to this CRM host's
 * `/sso/trade-system` to sign in.
 */
const chartEmbed: TechnicalEmbed = {
  getToken: (assetId) => fetchChartToken(assetId),
  via: 'crm',
  crmOrigin: window.location.origin,
};

/**
 * A chart, table or row of key figures the orchestrator built from the turn's
 * tool results, drawn by @paladigm/answer-visuals as the DEV-4197 design draws
 * it. Its own units and scale come with the payload, so the |y| < 1.5 percent
 * heuristic of `chart` blocks never sees it; a block the package cannot read
 * becomes its text table, then this Markdown.
 */
function VisualBlock({ block }: { block: ContentBlock }) {
  const { i18n } = useTranslation();
  const width = useVisualWidth();
  const theme = useDocumentTheme();
  return (
    <AnswerVisual
      block={block}
      width={width}
      theme={theme}
      locale={i18n.language || 'en'}
      renderText={(markdown) => <Markdown>{markdown}</Markdown>}
      embed={chartEmbed}
    />
  );
}

// ── text / document ──────────────────────────────────────────────────────────

function DocumentOrText({ block }: { block: ContentBlock }) {
  const content = safeString(block.payload?.content);
  if (!content) return null;
  const title = typeof block.metadata?.title === 'string' ? block.metadata.title : undefined;
  if (block.type === 'document') {
    return (
      <div className="rounded-lg border bg-card">
        {title && <p className="border-b px-4 py-2 text-sm font-medium">{title}</p>}
        <div className="max-h-[480px] overflow-y-auto p-4">
          <Markdown>{content}</Markdown>
        </div>
      </div>
    );
  }
  return <Markdown>{content}</Markdown>;
}

// ── metric_card / metric_group ──────────────────────────────────────────────

function MetricCard({ block }: { block: ContentBlock }) {
  const { t } = useTranslation('ai-cio');
  const p = block.payload ?? {};
  const m = block.metadata ?? {};
  const label = typeof m.label === 'string' ? m.label : '';
  const unit = typeof p.unit === 'string' ? p.unit : null;
  const delta = p.delta_pct ?? p.delta;
  return (
    <div className="rounded-lg border bg-card p-3">
      {label && <p className="text-xs text-muted-foreground">{label}</p>}
      <p className="mt-1 text-xl font-semibold text-foreground">
        {formatMetricValue(p.value, unit, t)}
      </p>
      {delta !== undefined && delta !== null && (
        <p className={cn('mt-0.5 text-xs', signClass(delta))}>
          {p.delta_pct !== undefined && p.delta_pct !== null
            ? `${Number(p.delta_pct) > 0 ? '+' : ''}${(Number(p.delta_pct) * 100).toFixed(2)}%`
            : `${Number(delta) > 0 ? '+' : ''}${Number(delta).toString()}`}
        </p>
      )}
    </div>
  );
}

function MetricGroup({ block }: { block: ContentBlock }) {
  const { t } = useTranslation('ai-cio');
  const items = Array.isArray(block.payload?.items) ? block.payload.items : [];
  const groupUnit = typeof block.metadata?.unit === 'string' ? block.metadata.unit : null;
  const label = typeof block.metadata?.label === 'string' ? block.metadata.label : undefined;
  return (
    <div className="rounded-lg border bg-card p-3">
      {label && <p className="mb-2 text-xs text-muted-foreground">{label}</p>}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {items.map((raw, i) => {
          const item = raw as Record<string, unknown>;
          const unit = typeof item.unit === 'string' ? item.unit : groupUnit;
          const caption = safeString(item.label ?? item.context);
          return (
            <div key={i}>
              <p className="text-base font-semibold text-foreground">
                {formatMetricValue(item.value, unit, t)}
              </p>
              {caption ? <p className="text-xs text-muted-foreground">{caption}</p> : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── table ──────────────────────────────────────────────────────────────────

interface ColumnDef {
  key: string;
  label: string;
  type?: string;
}

function TableBlock({ block }: { block: ContentBlock }) {
  const rows = Array.isArray(block.payload?.rows)
    ? (block.payload.rows as Record<string, unknown>[])
    : [];
  const title = typeof block.metadata?.title === 'string' ? block.metadata.title : undefined;
  const columns: ColumnDef[] =
    Array.isArray(block.metadata?.columns) && block.metadata.columns.length
      ? (block.metadata.columns as ColumnDef[])
      : Object.keys(rows[0] ?? {}).map((k) => ({ key: k, label: k }));

  if (!rows.length || !columns.length) return null;

  return (
    <div className="rounded-lg border bg-card">
      {title && <p className="border-b px-4 py-2 text-sm font-medium">{title}</p>}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c) => (
                <TableHead key={c.key}>{c.label}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow key={i}>
                {columns.map((c) => (
                  <TableCell
                    key={c.key}
                    className={c.type === 'percent' ? signClass(row[c.key]) : undefined}
                  >
                    {formatCell(row[c.key], c.type)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ── image / file / list / code ──────────────────────────────────────────────

function ImageBlock({ block }: { block: ContentBlock }) {
  const { t } = useTranslation('ai-cio');
  const p = block.payload ?? {};
  const alt =
    typeof block.metadata?.alt === 'string' ? block.metadata.alt : t('block.imageAlt', 'Image');
  // Inline bytes only. A URL image is a request the browser makes on sight, so
  // a block whose URL carried data would send it to that host without a click;
  // it renders as its alt text instead, like a markdown image (InertImage). The
  // orchestrator emits no URL image blocks today.
  if (typeof p.base64 === 'string' && p.base64) {
    return (
      <img
        src={`data:image/*;base64,${p.base64}`}
        alt={alt}
        className="max-w-full rounded-lg border"
      />
    );
  }
  if (typeof p.url === 'string' && p.url) return <InertImage alt={alt} />;
  return null;
}

function FileBlock({ block }: { block: ContentBlock }) {
  const { t } = useTranslation('ai-cio');
  const link = useVaultFileLink(block.vaultFileId);
  const expiresAt = linkExpiryMs(block.payload?.expires_at);
  // The link is signed for a limited time and a card outlives it (the thread is
  // kept in localStorage, and a tab stays open). `now` is the clock as of the
  // last check: read at mount, re-read by a timer at the expiry so an open tab
  // stops offering a dead link on time, and re-read on click, since a timer can
  // fire late after the machine sleeps.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (expiresAt === null || now >= expiresAt) return;
    // setTimeout's delay is a signed 32-bit int and a longer one fires at once, so
    // a far-off expiry is waited for in steps: each tick re-arms for the rest.
    const timer = setTimeout(
      () => {
        setNow(Date.now());
      },
      Math.min(expiresAt - now, 2 ** 31 - 1),
    );
    return () => {
      clearTimeout(timer);
    };
  }, [expiresAt, now]);
  const expired = expiresAt !== null && now >= expiresAt;
  const filename =
    typeof block.metadata?.filename === 'string' ? block.metadata.filename.trim() : '';
  const name = filename || t('block.download', 'Download');
  const sizeBytes = block.metadata?.size_bytes;
  // Never render an orchestrator-controlled URL into an href without checking
  // its scheme — a javascript:/data: value would be a click-to-XSS sink. Drop
  // the Download action when the scheme isn't http(s).
  const url = safeHref(block.payload?.url);
  const size = typeof sizeBytes === 'number' && Number.isFinite(sizeBytes) ? sizeBytes : undefined;
  if (link?.removed) {
    return (
      <FileCard
        state="unavailable"
        name={name}
        sizeBytes={size}
        announceUnavailable={link.removedOnOpen}
        className="w-full"
      />
    );
  }
  const liveUrl = url !== null && expiresAt !== null && !expired ? url : undefined;
  const downloadsFromVault = link !== null && link.canDownload && liveUrl === undefined;
  const href = link ? liveUrl : url !== null && !expired ? url : undefined;
  return (
    <FileCard
      state="result"
      name={name}
      sizeBytes={size}
      onOpen={
        link
          ? () => {
              void link.open();
            }
          : undefined
      }
      openTooltipSide="top"
      meta={
        url !== null && expired && !downloadsFromVault ? (
          <span className="whitespace-normal">
            {t('block.linkExpired', 'This link has expired. Ask the AI CIO again for a fresh one.')}
          </span>
        ) : undefined
      }
      downloadHref={href}
      onDownload={
        downloadsFromVault
          ? () => {
              link.download();
            }
          : href
            ? (event) => {
                if (expiresAt !== null && Date.now() >= expiresAt) {
                  event.preventDefault();
                  setNow(Date.now());
                  if (link?.canDownload) link.download();
                }
              }
            : undefined
      }
      className="w-full"
    />
  );
}

function ListBlock({ block }: { block: ContentBlock }) {
  const items = Array.isArray(block.payload?.items) ? block.payload.items : [];
  if (!items.length) return null;
  return (
    <ul className="list-disc ps-5 text-sm text-foreground">
      {items.map((it, i) => (
        <li key={i}>{safeString(it)}</li>
      ))}
    </ul>
  );
}

function CodeBlock({ block }: { block: ContentBlock }) {
  const content = safeString(block.payload?.content);
  if (!content) return null;
  return (
    <pre className="overflow-x-auto rounded-lg border bg-muted p-3 text-xs">
      <code>{content}</code>
    </pre>
  );
}

function AgentCard({ block }: { block: ContentBlock }) {
  const { t } = useTranslation('ai-cio');
  const p = block.payload ?? {};
  const heading = safeString(p.display_name ?? p.agent) || t('block.agent', 'Agent');
  const summary = typeof p.summary === 'string' ? p.summary : '';
  const sub = Array.isArray(p.sub_blocks) ? (p.sub_blocks as ContentBlock[]) : [];
  return (
    <div className="rounded-lg border bg-card p-3">
      <p className="text-sm font-medium text-foreground">{heading}</p>
      {summary && <p className="mt-1 text-sm text-muted-foreground">{summary}</p>}
      {sub.length > 0 && <BlockList blocks={sub} />}
    </div>
  );
}

function ErrorBlock({ block }: { block: ContentBlock }) {
  const { t } = useTranslation('ai-cio');
  const severity = block.metadata?.severity;
  const message =
    typeof block.payload?.message === 'string'
      ? block.payload.message
      : t('block.error', 'Response failed. Try again');
  return (
    <div
      className={cn(
        'rounded-lg border px-3 py-2 text-sm',
        severity === 'warning'
          ? 'border-warning/30 bg-warning/10 text-warning'
          : 'border-destructive/30 bg-destructive/10 text-destructive',
      )}
    >
      {message}
    </div>
  );
}
